import {
  BarColor,
  Box,
  Colors,
  InfoDot,
  StackedBar,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { Warning } from '@factorialco/f0-react/icons/app'

import MoneyWithCurrency from 'modules/core/lib/money'

import { Amount } from 'modules/project_management/components/Amount'
import { InsightCard } from 'modules/project_management/components/InsightCard'

interface BudgetStackedBarCardProps {
  billableToDate: number
  currency: string
  current: number
  currentLabel: string
  info: React.ReactNode
  locale: string
  planned: number
  plannedLabel: string
  remainingOrOverBudget: number
  showWarningTag?: boolean
  title: string
  warningTooltipLabel?: string
}

const BudgetStackedBarCard = ({
  billableToDate,
  currency,
  current,
  currentLabel,
  info,
  locale,
  planned,
  plannedLabel,
  remainingOrOverBudget,
  showWarningTag,
  title,
  warningTooltipLabel,
}: BudgetStackedBarCardProps) => {
  const [toggle, setToggle] = useToggle(false)
  const allocatedColor: BarColor = ['grey800', 'grey800']
  const overBudgetColor: BarColor = ['radicalRed1000', 'radicalRed1000']
  const remainingColor: BarColor = ['grey400', 'grey400']
  const allocatedDotColor = 'grey800'
  const overBudgetDotColor = 'radicalRed1000'
  const remainingDotColor = 'grey400'

  const dotLegend = (dotColor: Colors, legendLabel: string) => {
    return (
      <Box flexDirection="row" alignItems="center" gap="s6">
        <InfoDot size="small" color={dotColor} />
        <Text.Normal size="200">{legendLabel}</Text.Normal>
      </Box>
    )
  }

  return (
    <InsightCard title={title} info={info}>
      <Box flexDirection="column" gap="s12">
        <Box flexDirection="row" gap="s6" alignItems="center">
          <Amount
            amount={current}
            currency={currency}
            size="400"
            weight="semibold"
            color={current > planned ? 'moodSuperNegative' : 'grey1000'}
          />
          <Text size="200" weight="normal" color="grey1000">
            {`/ ${MoneyWithCurrency({
              amount: planned,
              currency,
              locale,
            })}`}
          </Text>
          {showWarningTag && (
            <Box paddingTop="s6">
              <Tooltip.Dialog
                label={warningTooltipLabel}
                open={toggle}
                toggle={setToggle}
                attachment="top center"
              >
                <F0Icon icon={Warning} color="warning" />
              </Tooltip.Dialog>
            </Box>
          )}
        </Box>
        <StackedBar>
          <StackedBar.Bar value={billableToDate} color={allocatedColor} />
          <StackedBar.Bar
            value={remainingOrOverBudget}
            color={planned >= current ? remainingColor : overBudgetColor}
          />
        </StackedBar>

        <Box flexDirection="row" alignItems="center" gap="s6">
          {dotLegend(allocatedDotColor, currentLabel)}
          {dotLegend(planned >= current ? remainingDotColor : overBudgetDotColor, plannedLabel)}
        </Box>
      </Box>
    </InsightCard>
  )
}

export default BudgetStackedBarCard
